import dayjs from 'dayjs';
import { computed, defineAsyncComponent, provide, ref } from 'vue';

import { useDashboards } from '@/app/dashboards/composables/useDashboards';
import { useReport } from '@/app/dashboards/composables/useReport';
import { useReportCategory } from '@/app/dashboards/composables/useReportCategory';
import constants, { DASHBOARD_FILTER_DETAILS } from '@/app/dashboards/constants';
import { REPORT_MENU_ITEMS } from '@/app/dashboards/reportConstants';
import {
  ALL_METRIC_REPORTS,
  BENCHMARK_REPORTS,
  CONTENT_REPORT_TYPES,
  isMultiMediaMetricReport,
} from '@/app/dashboards/utils/reports.enum';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useDashboardsSocialListeningStore } from '@/stores/dashboards-social-listening';
import { useFlagStore } from '@/stores/flag';
import { usePlatformStore } from '@/stores/platform';
import { formatDate } from '@/utils/dateUtils';

export function useReportMenu({ reportId, loading, serverErrorMessage } = {}) {
  const flagStore = useFlagStore();
  const dashboardsStore = useDashboardsStore();
  const dashboardReportsStore = useDashboardReportsStore();
  const dashboardsSocialListeningStore = useDashboardsSocialListeningStore();
  const platformStore = usePlatformStore();

  const { customMetricNotFound } = useCustomMetrics();

  const selectedReportingPeriod = ref({});
  const selectedComparisonPeriod = ref({});
  provide('selectedReportingPeriod', selectedReportingPeriod);
  provide('selectedComparisonPeriod', selectedComparisonPeriod);

  const report = computed(() => dashboardsStore.reports?.[reportId.value]);

  const {
    metricTypeReportKey,
    reportCompetitorIds,
    reportBrands,
    reportCategory,
    reportCompetitors,
    reportChannel,
    reportChannels,
    reportMetric,
    reportMetricDetails,
    reportType,
    topicFilterIds,
    topicComparisonFilterIds,
  } = useReport({
    report,
    loading,
    preview: ref(false),
  });

  const { isListeningReportCategory } = useReportCategory({ reportCategory });

  const { canAccessIndustryBenchmarks, showNoAccessToMetric } = useDashboards();

  const isMetricReport = computed(() => ALL_METRIC_REPORTS.includes(reportType.value));

  const isCustomMetricDeleted = computed(() => {
    if (report.value.meta.metrics?.[0].split('_')[0] !== 'CM') return false;
    return customMetricNotFound(report.value.meta.metrics[0]);
  });

  const dashboardCompetitiveIdentityBrandsByIds = computed(() =>
    dashboardsStore.dashboardCompetitiveIdentityBrands.map((brand) => brand.id),
  );

  const dashboardIdentityBrandIds = computed(() =>
    dashboardsStore.dashboardIdentityBrands.map((brand) => brand.id),
  );

  const canAccessYoutubeCompliance = computed(
    () => flagStore.ready && flagStore.flags?.youtubeCompliance,
  );

  const meetsYTCompliance = computed(
    () =>
      !reportChannels.value.includes(CHANNELS.YOUTUBE.value) || canAccessYoutubeCompliance.value,
  );

  const showJPEGInReportMenu = computed(
    () =>
      !isMultiMediaMetricReport(reportType.value) &&
      flagStore.ready &&
      flagStore.flags.downloadReportJpg &&
      meetsYTCompliance.value,
  );

  const noAccessToMetric = computed(() =>
    showNoAccessToMetric(reportMetricDetails.value, reportChannel.value, reportBrands.value),
  );

  const userHasNoAccessToAllReportCompetitors = computed(
    () =>
      reportCompetitors.value?.every(
        (competitor) => !reportCompetitorIds.value?.includes(competitor.id),
      ) ?? false,
  );

  const allReportBrandsHasNoAccessToCompetitors = computed(
    () =>
      !reportBrands.value?.some((brand) =>
        dashboardCompetitiveIdentityBrandsByIds.value.includes(brand.id),
      ),
  );

  const userHasNoAccessToAllReportBrands = computed(
    () =>
      reportBrands.value?.every((brand) => !dashboardIdentityBrandIds.value.includes(brand.id)) ??
      false,
  );

  const userHasNoAccess = computed(() => {
    if (isListeningReportCategory.value) {
      const topicIdList = dashboardsSocialListeningStore.topicListForMultipleOrganizations.map(
        (topic) => topic.id,
      );
      const comparisonIdList =
        dashboardsSocialListeningStore.comparisonListForMultipleOrganizations.map(
          (comparison) => comparison.id,
        );
      return (
        !topicFilterIds.value?.some((topicId) => topicIdList.includes(topicId)) &&
        !topicComparisonFilterIds.value?.some((comparisonId) =>
          comparisonIdList.includes(comparisonId),
        )
      );
    }
    if (reportBrands.value.length && reportCompetitors.value.length) {
      return (
        userHasNoAccessToAllReportCompetitors.value &&
        (allReportBrandsHasNoAccessToCompetitors.value || userHasNoAccessToAllReportBrands.value)
      );
    }
    if (!reportBrands.value.length && reportCompetitors.value) {
      return userHasNoAccessToAllReportCompetitors.value;
    }
    return userHasNoAccessToAllReportBrands.value;
  });

  const isInvalid = computed(() => {
    if (isListeningReportCategory.value) return false;
    if (!reportBrands.value.length && reportCompetitors.value.length) return false;
    const invalid = reportBrands.value.every((brand) =>
      reportChannels.value.every(
        (channel) =>
          channel !== CHANNELS.TSI_CHANNELS.value &&
          !platformStore.hasCompletePlatformConnection(brand.id, channel),
      ),
    );
    return invalid;
  });

  const invalidBenchmarkAccess = computed(() => {
    const isBenchmarkReport = BENCHMARK_REPORTS.includes(report.value?.type);
    return isBenchmarkReport && !canAccessIndustryBenchmarks.value;
  });

  const noPostsInPeriod = computed(() => {
    if (CONTENT_REPORT_TYPES.includes(report.value?.type)) {
      return report.value.data?.content?.length === 0;
    }
    return false;
  });

  const hasServerError = computed(() => !!serverErrorMessage?.value);

  const disableCSV = computed(
    () =>
      hasServerError.value ||
      userHasNoAccess.value ||
      noAccessToMetric.value ||
      isInvalid.value ||
      invalidBenchmarkAccess.value ||
      noPostsInPeriod.value ||
      isCustomMetricDeleted.value,
  );

  const disableJPEG = computed(
    () => hasServerError.value || userHasNoAccess.value || isInvalid.value || noPostsInPeriod.value,
  );

  const staticDates = computed(() => report.value?.meta?.static_dates);

  const hasStaticDates = computed(
    () =>
      !!report.value?.meta?.static_dates?.start_date &&
      !!report.value?.meta?.static_dates?.end_date,
  );

  function handleRollingTime() {
    const today = dayjs().startOf('day');
    const originalStart = dayjs(staticDates.value?.start_date).startOf('day');
    const originalEnd = dayjs(staticDates.value?.end_date).startOf('day');
    const offsetDays = today.diff(originalEnd, 'day');

    return {
      start: dayjs(originalStart).add(offsetDays, 'day').toDate(),
      end: dayjs(originalEnd).add(offsetDays, 'day').toDate(),
    };
  }

  const customDateReportingPeriod = computed(() => ({
    preset: constants.REPORTING_PERIOD_FILTERS.CUSTOM.value,
    range: staticDates.value?.rolling_time
      ? handleRollingTime()
      : {
          start: staticDates.value?.start_date ?? dashboardReportsStore.reportStartDate,
          end: staticDates.value?.end_date ?? dashboardReportsStore.reportEndDate,
        },
  }));

  const customDateComparisonPeriod = computed(() => ({
    preset: constants.COMPARISON_PERIOD_FILTERS.CUSTOM.value,
    range: {
      start: staticDates.value?.context_start_date ?? dashboardReportsStore.contextStartDate,
      end: staticDates.value?.context_end_date ?? dashboardReportsStore.contextEndDate,
    },
  }));

  function handleSaveStaticDates() {
    const params = {
      reportId: report.value.id,
      reportDateRange: [
        formatDate(selectedReportingPeriod.value.start, 'YYYY-MM-DD'),
        formatDate(selectedReportingPeriod.value.end, 'YYYY-MM-DD'),
      ],
    };
    if (isMetricReport.value) {
      params.contextDateRange = [
        formatDate(selectedComparisonPeriod.value.start, 'YYYY-MM-DD'),
        formatDate(selectedComparisonPeriod.value.end, 'YYYY-MM-DD'),
      ];
    }
    dashboardsStore.saveReportStaticDates(params);
  }

  const canModifyCustomDates = computed(
    () => !hasServerError.value && !userHasNoAccess.value && !isCustomMetricDeleted.value,
  );

  const customizeDatesMenuItem = computed(() => {
    const comparisonOptions = isMetricReport.value
      ? DASHBOARD_FILTER_DETAILS.DASHBOARD.options.comparisonPeriod
      : undefined;

    const menuItem = {
      ...REPORT_MENU_ITEMS.CUSTOMIZE_DATE,
      roles: [constants.ROLES.OWNER.value, constants.ROLES.EDITOR.value],
      form: defineAsyncComponent(() => import('@/components/dashingUI/DateRangePicker.vue')),
      formProps: {
        reportingPeriod: customDateReportingPeriod.value,
        comparisonPeriod: customDateComparisonPeriod.value,
        comparisonOptions,
      },
      command: () => handleSaveStaticDates(),
      disabled: hasServerError.value || userHasNoAccess.value || isCustomMetricDeleted.value,
      tooltip:
        hasServerError.value || userHasNoAccess.value || isCustomMetricDeleted.value
          ? 'Custom dates are not available for this report'
          : undefined,
    };
    if (hasStaticDates.value) {
      menuItem.badge = 1;
    }
    return menuItem;
  });

  const deleteCommandProps = computed(() => {
    const {
      confirmDeleteTitle,
      confirmDeleteButtonLabel,
      confirmDeleteMessage,
      deleteErrorMessage,
      deleteSuccessMessage,
      acceptSeverity,
    } = REPORT_MENU_ITEMS[report.value?.type] || REPORT_MENU_ITEMS.DELETE;
    return {
      confirmDeleteTitle,
      confirmDeleteButtonLabel,
      confirmDeleteMessage,
      deleteErrorMessage,
      deleteSuccessMessage,
      acceptSeverity,
    };
  });

  return {
    customizeDatesMenuItem,
    deleteCommandProps,
    disableCSV,
    disableJPEG,
    hasStaticDates,
    meetsYTCompliance,
    showJPEGInReportMenu,
    canModifyCustomDates,
  };
}
